import { useMemo, useState, useEffect } from 'react';
import SonarSwitch from '../../SonarSwitch';
import { useAuth } from '../../../../utils/Auth';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { transition, variants } from './variants';
import { getQrDataFieldType } from '../../../../lib/getQrDataFieldType';
import API from '../../../../API';
import { useUserPermissions } from '../../../../utils/hooks/useUserPermissions';
import styles from './Actions.module.css';

export default function Actions() {

    const { qr_data, liquidQRs, dataDispatch, optionsDispatch } = useAuth();
    const { canEditQR } = useUserPermissions();
    const { deactivateQR, unlinkQr } = API;
    const navigate = useNavigate();

    const qr_type = useMemo(() => qr_data?.data?.qr_type || '', [qr_data?.data?.qr_type]);
    const isLinked = useMemo(() => qr_data?.data?.linked_qr, [qr_data?.data?.linked_qr]);
    const parentData = useMemo(() => liquidQRs?.find(qr => qr?.id === qr_data?.data?.linked_qr), [liquidQRs, qr_data?.data?.linked_qr]);
    const qrDataType = getQrDataFieldType(qr_type);

    const [ unlink, setUnlink ] = useState(false);
    const [ remove, setRemove ] = useState(false);
    const [ loading, setLoading ] = useState(false);

    const handleUnlinkQr = async () => {
        if (!canEditQR) {
            alert('You do not have permission to edit QR codes.');
            return;
        }
        const data = {
            qrId: qr_data?.id,
            parentId: parentData?.id
        }

        try {
            await unlinkQr(data)
        } catch (err) {
            console.log(err);
        }
        
        dataDispatch('unlink', {parent: parentData, qrStyle: qr_data?.data?.qr_style});
        optionsDispatch('unlink', qr_data?.data?.qr_style);
        navigate(`/collection/${qr_data?.id}/edit`);
    };

    const handleDeleteQr = async () => {
        if (!canEditQR) {
            alert('You do not have permission to edit QR codes.');
            return;
        }
        setLoading(true);
        try {
            // if ((qrDataType === 'pdf' || qrDataType === 'asset' || qrDataType === 'menu') && (qr_data?.data?.[qrDataType]?.files?.length || []) > 1) {
            //     await deleteFiles({ qrId: qr_data.id, files: qr_data.data?.[qrDataType]?.files?.map(f => f.path) });
            // };

            await deactivateQR(qr_data?.id);
            navigate('/collection');
        } catch (err) {
            console.error(err);
        } finally {
            setRemove(false);
            setLoading(false);
        };
    };

    useEffect(() => {
        let timer;
        if (remove || unlink) {
            timer = setTimeout(() => {
                setRemove(false);
                setUnlink(false);
            },3000);
        };

        return () => timer && clearTimeout(timer);
    }, [remove, unlink]);

    const handleVisitQR = () => {
        switch (qr_data.data.qr_type) {
            case 'link':
            case 'youtube':
            case 'tiktok':
            case 'linkedin':
            case 'google-review':
            case 'review-link':
            case 'appointment':
            case 'flyers':
                return window.open(qr_data.data.redirect_url, '_blank');
            case 'location':
                return window.open(`https://www.google.com/maps/place/${qr_data.data.location.lat},${qr_data.data.location.lng}`, '_blank');
            case 'assets':
            case 'inventory':
            case 'container-label':
                return window.open(`${window.location.origin}/a/${qr_data.id}`, '_blank');
            case 'tracker':
            case 'workout-log':
            case 'service-log':
            case 'vehicle-log':
            case 'maintenance-tracker':
            case 'liquid-label':
			case 'garden-label':
				return window.open(`${window.location.origin}/t/${qr_data.id}`, '_blank');
			case 'liquid-poster':
			case 'for-sale':
				return window.open(`${window.location.origin}/poster/${qr_data.id}`, '_blank');
            case 'survey':
            case 'review':
            case 'bug-report':
            case 'contact-form':
                return window.open(`${window.location.origin}/s/${qr_data.id}`, '_blank');
            case 'pdf':
            case 'brochure':
            case 'user-manual':
                return window.open(`${window.location.origin}/p/${qr_data.id}`, '_blank');
            case 'microsite':
            case 'landing-page':
                return window.open(`${window.location.origin}/site/${qr_data.id}`, '_blank');
            case 'menu':
                return window.open(`${window.location.origin}/m/${qr_data.id}`, '_blank');
            case 'connect':
                return window.open(`${window.location.origin}/c/${qr_data.id}`, '_blank');
            case 'dbc':
            case 'bitsignal':
            default:
                break;  
        };
    };

    return (
        <div className={styles.container}>
            <h5 className={styles.header}>Actions</h5>
            <div className={styles.words}>
                Ready to manage your QR? Here you can control its features. 
                <strong style={{marginLeft: '.25rem'}}>Sonar Mode</strong> is a special setting that changes your QR's behavior. 
                When enabled, scanning the code will take you directly to edit.
                <div>
                    You can test your QR's current settings using the two scan options below:
                    <ul>
                        <li>
                            <strong>Scan QR</strong>: Use this button to instantly test where the code currently directs users. This scan will take you directly to the destination without logging any analytics data.
                        </li>
                        <li>
                            <strong>Simulate Scan</strong>: Use this button to test the destination <em>and</em> record the action as a normal scan in your analytics, allowing you to verify data tracking is working correctly.
                        </li>
                    </ul>
                </div>                 
                If the QR is no longer needed, you can <strong>delete it</strong> to permanently remove it from your account.
            </div>
            <div className={styles.wrap}>
                {canEditQR && <SonarSwitch />}
                <div className={styles.scanContainer}>
                    <p className={styles.scanTitle}>
                        Scan Options
                    </p>
                    <button 
                        className={styles.scanBtn} 
                        onClick={handleVisitQR}
                        disabled={qr_data.data.qr_type === 'dbc' || qr_data.data.qr_type === 'bitsignal'}
                    >
                        <i className='far fa-qrcode-read' />
                        Visit QR
                    </button>
                    <button
                        className={styles.scanBtn}
                        onClick={() => window.open(`https://lqr.ai/${qr_data?.id}`, '_blank')}
                    >
                        <i className='far fa-qrcode-read' />
                        Simulate Scan
                    </button>
                </div>
                {isLinked && (
                    <div className={styles.unlinkWrap}>
                        <motion.button
                            key='unlink/cancel'
                            className={styles.deleteBtn}
                            transition={transition}
                            variants={variants}
                            initial={false}
                            animate={!unlink ? 'initial1' : 'animate1'}
                            onClick={() => setUnlink(!unlink)}
                            disabled={loading || !canEditQR}
                            title={!canEditQR ? 'You do not have permission to edit QR codes.' : undefined}
                        >
                            {!unlink ? <>Unlink QR <i className='far fa-object-subtract' /></> : 'Cancel'}
                        </motion.button>
                        <motion.button
                            key='unklinkConfirm'
                            className={styles.confirmBtn}
                            transition={transition}
                            variants={variants}
                            initial={false}
                            animate={unlink ? 'animate2' : 'initial2'}
                            onClick={handleUnlinkQr}
                            disabled={loading || !canEditQR}
                            title={!canEditQR ? 'You do not have permission to edit QR codes.' : undefined}
                        >
                            Confirm
                            <i className='far fa-object-subtract' />
                        </motion.button>
                    </div>
                )}
                <div className={styles.deleteWrap}>
                    <motion.button
                        key='remove/cancel'
                        className={styles.deleteBtn}
                        transition={transition}
                        variants={variants}
                        initial={false}
                        animate={!remove ? 'initial1' : 'animate1'}
                        onClick={() => setRemove(!remove)}
                        disabled={loading || !canEditQR}
                        title={!canEditQR ? 'You do not have permission to edit QR codes.' : undefined}
                    >
                        {!remove ? <><i className='far fa-trash-can' /> Delete QR</> : <>< i className='far fa-xmark' /> Cancel</>}
                    </motion.button>
                    <motion.button
                        key='deleteConfirm'
                        className={styles.confirmBtn}
                        transition={transition}
                        variants={variants}
                        initial={false}
                        animate={remove ? 'animate2' : 'initial2'}
                        onClick={handleDeleteQr}
                        disabled={loading || !canEditQR}
                        title={!canEditQR ? 'You do not have permission to edit QR codes.' : undefined}
                    >
                        <i className='far fa-trash-can' />
                        Confirm
                    </motion.button>
                </div>
            </div>
        </div>
    )
}
